// Language dictionary. Foreign + Indian regional languages.
export const languages = [
  { code: "en", label: "English", region: "Global" },
  { code: "es", label: "Español", region: "Foreign" },
  { code: "fr", label: "Français", region: "Foreign" },
  { code: "de", label: "Deutsch", region: "Foreign" },
  { code: "ar", label: "العربية", region: "Foreign" },
  { code: "zh", label: "中文", region: "Foreign" },
  { code: "pt", label: "Português", region: "Foreign" },
  { code: "ja", label: "日本語", region: "Foreign" },
  { code: "hi", label: "हिन्दी", region: "India" },
  { code: "ta", label: "தமிழ்", region: "India" },
  { code: "te", label: "తెలుగు", region: "India" },
  { code: "bn", label: "বাংলা", region: "India" },
  { code: "mr", label: "मराठी", region: "India" },
  { code: "gu", label: "ગુજરાતી", region: "India" },
  { code: "kn", label: "ಕನ್ನಡ", region: "India" },
  { code: "ml", label: "മലയാളം", region: "India" },
  { code: "pa", label: "ਪੰਜਾਬੀ", region: "India" },
  { code: "or", label: "ଓଡ଼ିଆ", region: "India" },
];

const en = {
  // Auth / Nav
  get_started: "Get started", sign_in: "Sign in", sign_up: "Sign up",
  email: "Email", password: "Password", full_name: "Full name", company: "Company",
  or_continue: "or continue with", google_login: "Continue with Google", forgot_password: "Forgot password?",
  logout: "Sign out", dashboard: "Dashboard", settings: "Settings", billing: "Subscription",

  // Landing hero
  hero_pill: "AI construction OS · Trial 14 days free",
  hero_title_a: "The precision", hero_title_b: "operating system", hero_title_c: "for modern construction sites.",
  hero_desc: "AI-verified site logs, face + GPS worker attendance, and real-time safety alerts — all in one industrial-grade platform, built for global builders.",
  start_free_14: "Start free for 14 days",
  stat_ai_models: "Specialised AI models", stat_languages: "Languages supported",
  stat_servers: "Servers you'll manage", stat_uptime: "Uptime SLA",

  // Quote
  quote_a: "Software should meet workers where they are —",
  quote_b: "in their language, on their phone",
  quote_c: "— not force them into another dashboard.",
  quote_source: "— Setubuild.OS design principle no. 01",

  // Features section
  features_h_a: "Every discipline.", features_h_b: "One command center.",
  f_inspector: "AI Photo Inspector",
  f_inspector_d: "Vision analyses site photos for hazards, PPE gaps, defects and compliance in seconds — with a confidence score.",
  f_chat: "Ask Setu Copilot",
  f_chat_d: "Data-aware AI copilot connected to your projects, attendance, DPRs, inventory and safety. Ask 'which project is delayed?' or 'M25 mix design'.",
  f_reports: "Auto Daily Reports",
  f_reports_d: "Structured DPRs generated from your labor, weather and activity feed — ready to approve and share.",
  f_attendance: "Face + GPS Attendance",
  f_attendance_d: "Fraud-proof worker attendance. Face verification runs on device, GPS captured on every punch.",
  f_voice: "Voice Site Logs",
  f_voice_d: "Speak your update, get an instant transcript in your language, logged to the right project.",
  f_safety: "Safety & Incidents",
  f_safety_d: "Real-time incident feed, severity triage, and audit-ready resolution tracking.",
  f_equipment: "QR Equipment",
  f_equipment_d: "Every tool gets a scannable code — check-in, checkout, location and health — all logged.",
  f_weather: "Weather Scheduler",
  f_weather_d: "Site workability overlay so rain never surprises the crew again.",

  // Visual break
  break_h_a: "Concrete cures. Cranes swing.", break_h_b: "Data flows.",
  break_p: "Every hour on site produces signal. We turn it into decisions — automatically, in your language, on your phone.",

  // Pricing
  pricing_h_a: "Priced for crews", pricing_h_b: "of every size.",
  pricing_desc: "Start free for 14 days. No credit card. Cancel anytime. Yearly plans include two months free.",
  plan: "Plan", monthly: "Monthly", yearly: "Yearly", subscribe_now: "Subscribe now",
  choose_plan: "Choose your plan", start_with: "Start with",
  starter: "Starter", pro: "Pro", enterprise: "Enterprise", badge_popular: "Most popular",
  p_starter_1: "1 project", p_starter_2: "5 team members", p_starter_3: "50 AI scans / mo", p_starter_4: "Email support",
  p_pro_1: "10 projects", p_pro_2: "50 team members", p_pro_3: "500 AI scans / mo", p_pro_4: "Voice logs + QR + Attendance", p_pro_5: "Priority support",
  p_ent_1: "Unlimited projects", p_ent_2: "Unlimited team", p_ent_3: "Unlimited AI", p_ent_4: "SSO + Audit logs", p_ent_5: "Dedicated CSM",

  // Final CTA
  cta_h_a: "Ship your next site", cta_h_b: "with precision.",
  cta_desc: "Join builders shipping safer, faster and smarter with AI at their side.",

  // Footer
  about: "About", contact: "Contact", privacy: "Privacy", terms: "Terms", refund: "Refund", faq: "FAQ",

  // Trial & subscription notices
  trial_active: "Trial active", trial_ends_in: "Trial ends in {days} days", trial_expired: "Trial expired — upgrade to continue",

  // App-level (existing)
  photo_inspector: "AI Photo Inspector", civil_ai: "Ask Setu Copilot", daily_reports: "Daily Reports",
  inventory: "Material Inventory", safety: "Safety & Alerts", team: "Team", equipment: "Equipment QR", weather: "Weather",
  projects: "Projects", active_workers: "Active Workers", budget_spent: "Budget Spent", ai_scans: "AI Scans", incidents: "Incidents", safety_score: "Safety Score",
  upload_photo: "Upload site photo", analyze: "Analyze", send: "Send", generate_report: "Generate Report",
  add_incident: "Report Incident", add_member: "Add Team Member", add_equipment: "Add Equipment", add_material: "Add Material",
  new_project: "New project",

  // Dashboard headline & sections
  overview: "Overview", greeting: "Good to see you, back on site.",
  milestones: "Milestones", milestone_progress: "Milestone Progress",
  no_milestones: "No milestones yet",
  create_first_project_hint: "Create your first project to start tracking milestones.",
  create_project: "Create project",
  activity: "Activity", recent_ai_signals: "Recent AI Signals",
  no_ai_activity: "No AI activity yet",
  no_ai_activity_hint: "Upload a site photo or ask the Copilot to see signals here.",
  your_projects: "Your Projects", total: "total",
  no_projects: "No projects yet",
  no_projects_hint: "Create your first project — it takes 30 seconds.",
  create_project_title: "Create a project",
  project_name: "Project name", client_label: "Client", location_label: "Location",
  budget_label: "Budget", target_date: "Target date", description: "Description",
  creating: "Creating…", signed_in_as: "Signed in as",

  // Nav labels (were hardcoded in AppLayout)
  nav_copilot: "Ask Setu Copilot", nav_boq: "Drawing & BOQ",
  nav_delay: "Delay Prediction", nav_attendance: "Attendance", nav_voice: "Voice Logs",
};

const stub = (native = {}) => ({ ...en, ...native });

export const translations = {
  en,

  hi: stub({
    get_started: "शुरू करें", sign_in: "साइन इन", sign_up: "साइन अप", logout: "लॉग आउट",
    dashboard: "डैशबोर्ड", settings: "सेटिंग्स", billing: "सदस्यता",
    email: "ईमेल", password: "पासवर्ड", full_name: "पूरा नाम", company: "कंपनी",
    hero_pill: "AI कंस्ट्रक्शन OS · 14 दिन मुफ्त ट्रायल",
    hero_title_a: "आधुनिक निर्माण साइटों के लिए", hero_title_b: "सटीक", hero_title_c: "ऑपरेटिंग सिस्टम।",
    hero_desc: "AI-सत्यापित साइट लॉग, फेस + GPS श्रमिक उपस्थिति, और रीयल-टाइम सुरक्षा अलर्ट — एक ही औद्योगिक-ग्रेड प्लेटफ़ॉर्म पर, वैश्विक बिल्डरों के लिए बनाया गया।",
    start_free_14: "14 दिन मुफ्त शुरू करें",
    stat_ai_models: "विशेष AI मॉडल", stat_languages: "समर्थित भाषाएं", stat_servers: "आपके सर्वर शून्य", stat_uptime: "अपटाइम SLA",
    quote_a: "सॉफ़्टवेयर को श्रमिकों तक पहुँचना चाहिए —", quote_b: "उनकी भाषा में, उनके फोन पर", quote_c: "— न कि उन्हें एक और डैशबोर्ड में धकेला जाए।",
    quote_source: "— Setubuild.OS डिज़ाइन सिद्धांत क्रम 01",
    features_h_a: "हर विभाग।", features_h_b: "एक कमांड सेंटर।",
    f_inspector: "AI फोटो निरीक्षक",
    f_inspector_d: "विज़न सेकंडों में साइट फ़ोटो का विश्लेषण करता है — खतरे, PPE कमी, दोष और अनुपालन के लिए, विश्वास स्कोर के साथ।",
    f_chat: "सिविल AI विशेषज्ञ",
    f_chat_d: "स्ट्रक्चरल डिज़ाइन, सामग्री गणना, और कोड अनुपालन (IS, ACI, यूरोकोड) पर तत्काल उत्तर।",
    f_reports: "स्वचालित दैनिक रिपोर्ट",
    f_reports_d: "आपके श्रम, मौसम और गतिविधि फ़ीड से बनी संरचित DPR — अनुमोदन और साझा करने के लिए तैयार।",
    f_attendance: "चेहरा + GPS उपस्थिति",
    f_attendance_d: "फ्रॉड-प्रूफ श्रमिक उपस्थिति। फेस वेरिफिकेशन डिवाइस पर, हर पंच पर GPS।",
    f_voice: "वॉइस साइट लॉग",
    f_voice_d: "अपडेट बोलिए, अपनी भाषा में तुरंत ट्रांसक्रिप्ट पाइए, सही प्रोजेक्ट में लॉग।",
    f_safety: "सुरक्षा और घटनाएं", f_safety_d: "रीयल-टाइम घटना फ़ीड, गंभीरता ट्राइज, और ऑडिट-रेडी समाधान ट्रैकिंग।",
    f_equipment: "QR उपकरण", f_equipment_d: "हर उपकरण को स्कैन कोड — चेक-इन, चेकआउट, स्थान और स्वास्थ्य — सब लॉग।",
    f_weather: "मौसम शेड्यूलर", f_weather_d: "साइट कार्य-योग्यता ओवरले, ताकि बारिश कभी दल को चौंकाए नहीं।",
    break_h_a: "कंक्रीट सूखता है। क्रेन घूमती है।", break_h_b: "डेटा बहता है।",
    break_p: "साइट पर हर घंटा संकेत पैदा करता है। हम उसे निर्णय में बदलते हैं — स्वचालित, आपकी भाषा में, आपके फोन पर।",
    pricing_h_a: "हर आकार के दलों के लिए", pricing_h_b: "सही कीमत।",
    pricing_desc: "14 दिन मुफ्त शुरू करें। कोई क्रेडिट कार्ड नहीं। कभी भी रद्द करें। वार्षिक प्लान में दो महीने मुफ्त शामिल।",
    plan: "प्लान", monthly: "मासिक", yearly: "वार्षिक", subscribe_now: "अभी सदस्यता लें", choose_plan: "अपना प्लान चुनें", start_with: "शुरू करें",
    badge_popular: "सबसे लोकप्रिय",
    p_starter_1: "1 प्रोजेक्ट", p_starter_2: "5 टीम सदस्य", p_starter_3: "50 AI स्कैन / माह", p_starter_4: "ईमेल सहायता",
    p_pro_1: "10 प्रोजेक्ट", p_pro_2: "50 टीम सदस्य", p_pro_3: "500 AI स्कैन / माह", p_pro_4: "वॉइस + QR + उपस्थिति", p_pro_5: "प्राथमिकता सहायता",
    p_ent_1: "असीमित प्रोजेक्ट", p_ent_2: "असीमित टीम", p_ent_3: "असीमित AI", p_ent_4: "SSO + ऑडिट लॉग", p_ent_5: "समर्पित CSM",
    cta_h_a: "अपनी अगली साइट", cta_h_b: "सटीकता से डिलीवर करें।",
    cta_desc: "उन बिल्डरों में शामिल हों जो AI के साथ अधिक सुरक्षित, तेज और स्मार्ट काम कर रहे हैं।",
    about: "हमारे बारे में", contact: "संपर्क", privacy: "गोपनीयता", terms: "शर्तें", refund: "रिफंड", faq: "प्रश्नोत्तर",
    trial_active: "ट्रायल सक्रिय", trial_ends_in: "{days} दिन में ट्रायल समाप्त", trial_expired: "ट्रायल समाप्त — जारी रखने के लिए अपग्रेड करें",
    // Dashboard
    overview: "अवलोकन", greeting: "आपको देख कर अच्छा लगा, वापस साइट पर।",
    milestones: "माइलस्टोन", milestone_progress: "माइलस्टोन प्रगति",
    no_milestones: "अभी कोई माइलस्टोन नहीं",
    create_first_project_hint: "माइलस्टोन ट्रैक करने के लिए अपना पहला प्रोजेक्ट बनाइए।",
    create_project: "प्रोजेक्ट बनाएं",
    activity: "गतिविधि", recent_ai_signals: "हाल के AI संकेत",
    no_ai_activity: "अभी कोई AI गतिविधि नहीं",
    no_ai_activity_hint: "साइट फोटो अपलोड करें या कोपायलट से पूछें।",
    your_projects: "आपके प्रोजेक्ट", total: "कुल",
    no_projects: "अभी कोई प्रोजेक्ट नहीं",
    no_projects_hint: "अपना पहला प्रोजेक्ट बनाइए — केवल 30 सेकंड।",
    create_project_title: "प्रोजेक्ट बनाएं",
    project_name: "प्रोजेक्ट का नाम", client_label: "क्लाइंट", location_label: "स्थान",
    budget_label: "बजट", target_date: "लक्ष्य तिथि", description: "विवरण",
    creating: "बना रहे हैं…", signed_in_as: "साइन इन के रूप में",
    new_project: "नया प्रोजेक्ट",
    photo_inspector: "AI फोटो निरीक्षक", civil_ai: "सिविल AI", daily_reports: "दैनिक रिपोर्ट",
    inventory: "इन्वेंटरी", safety: "सुरक्षा", team: "टीम", equipment: "उपकरण QR", weather: "मौसम",
    projects: "प्रोजेक्ट", active_workers: "सक्रिय कर्मचारी", budget_spent: "खर्च", safety_score: "सुरक्षा स्कोर",
    nav_copilot: "सेतु कोपायलट पूछें", nav_boq: "ड्राइंग व BOQ",
    nav_delay: "देरी पूर्वानुमान", nav_attendance: "उपस्थिति", nav_voice: "आवाज़ लॉग",
  }),

  mr: stub({
    get_started: "सुरू करा", sign_in: "साइन इन", sign_up: "साइन अप", logout: "लॉगआउट",
    dashboard: "डॅशबोर्ड", settings: "सेटिंग्ज", billing: "सबस्क्रिप्शन",
    email: "ईमेल", password: "पासवर्ड", full_name: "पूर्ण नाव", company: "कंपनी",
    hero_pill: "AI बांधकाम OS · 14 दिवस मोफत ट्रायल",
    hero_title_a: "आधुनिक बांधकाम साइटांसाठी", hero_title_b: "अचूक", hero_title_c: "ऑपरेटिंग सिस्टम.",
    hero_desc: "AI-सत्यापित साइट लॉग, चेहरा + GPS कामगार हजेरी, आणि रीअल-टाइम सुरक्षा अलर्ट — एकाच औद्योगिक प्लॅटफॉर्मवर, जागतिक बिल्डर्ससाठी.",
    start_free_14: "14 दिवस मोफत सुरू करा",
    stat_ai_models: "विशेष AI मॉडेल्स", stat_languages: "समर्थित भाषा", stat_servers: "तुमचे सर्व्हर शून्य", stat_uptime: "अपटाइम SLA",
    quote_a: "सॉफ्टवेअरने कामगारांकडे जावे —", quote_b: "त्यांच्या भाषेत, त्यांच्या फोनवर", quote_c: "— त्यांना दुसऱ्या डॅशबोर्डमध्ये ढकलू नये.",
    quote_source: "— Setubuild.OS डिझाइन तत्त्व क्र. 01",
    features_h_a: "प्रत्येक विभाग.", features_h_b: "एक कमांड सेंटर.",
    f_inspector: "AI फोटो निरीक्षक", f_inspector_d: "व्हिजन काही सेकंदात साइट फोटोंचे विश्लेषण करते — धोके, PPE कमी, दोष आणि अनुपालनासाठी.",
    f_chat: "सिव्हिल AI तज्ञ", f_chat_d: "स्ट्रक्चरल डिझाइन, मटेरियल कॅल्क्युलेशन आणि कोड अनुपालनावर तात्काळ उत्तरे.",
    f_reports: "स्वयंचलित दैनिक अहवाल", f_reports_d: "आपल्या श्रम, हवामान आणि क्रियाकलाप फीडवरून तयार केलेले DPR.",
    f_attendance: "चेहरा + GPS हजेरी", f_attendance_d: "फसवणूक-प्रूफ कामगार हजेरी. फेस व्हेरिफिकेशन डिव्हाइसवर, प्रत्येक पंचवर GPS.",
    f_voice: "व्हॉईस साइट लॉग", f_voice_d: "अपडेट बोला, तुमच्या भाषेत तात्काळ ट्रान्सक्रिप्ट, योग्य प्रोजेक्टमध्ये लॉग.",
    f_safety: "सुरक्षा आणि घटना", f_safety_d: "रीअल-टाइम घटना फीड, गंभीरता आणि ऑडिट-रेडी ट्रॅकिंग.",
    f_equipment: "QR उपकरणे", f_equipment_d: "प्रत्येक साधनाला स्कॅन कोड — चेक-इन, चेकआउट, स्थान.",
    f_weather: "हवामान शेड्युलर", f_weather_d: "साइट कार्य-क्षमता ओव्हरले जेणेकरून पाऊस दलाला आश्चर्यचकित करणार नाही.",
    break_h_a: "काँक्रीट सुकते. क्रेन फिरते.", break_h_b: "डेटा वाहतो.",
    break_p: "साइटवरील प्रत्येक तास सिग्नल तयार करतो. आम्ही तो निर्णयात रूपांतरित करतो.",
    pricing_h_a: "प्रत्येक आकाराच्या दलांसाठी", pricing_h_b: "योग्य किंमत.",
    pricing_desc: "14 दिवस मोफत सुरू करा. कोणतेही क्रेडिट कार्ड नाही. कधीही रद्द करा.",
    plan: "प्लॅन", monthly: "मासिक", yearly: "वार्षिक", subscribe_now: "आता सदस्यता घ्या", choose_plan: "तुमचा प्लॅन निवडा", start_with: "सुरू करा",
    badge_popular: "सर्वात लोकप्रिय",
    p_starter_1: "1 प्रकल्प", p_starter_2: "5 टीम सदस्य", p_starter_3: "50 AI स्कॅन / महिना", p_starter_4: "ईमेल सपोर्ट",
    p_pro_1: "10 प्रकल्प", p_pro_2: "50 टीम सदस्य", p_pro_3: "500 AI स्कॅन / महिना", p_pro_4: "व्हॉईस + QR + हजेरी", p_pro_5: "प्राथमिकता सपोर्ट",
    p_ent_1: "अमर्यादित प्रकल्प", p_ent_2: "अमर्यादित टीम", p_ent_3: "अमर्यादित AI", p_ent_4: "SSO + ऑडिट लॉग", p_ent_5: "समर्पित CSM",
    cta_h_a: "आपली पुढील साइट", cta_h_b: "अचूकतेने पूर्ण करा.",
    cta_desc: "AI च्या मदतीने सुरक्षित, वेगवान आणि हुशारीने बांधकाम करणाऱ्या बिल्डर्समध्ये सामील व्हा.",
    about: "आमच्याबद्दल", contact: "संपर्क", privacy: "गोपनीयता", terms: "अटी", refund: "परतावा",
  }),

  es: stub({
    get_started: "Empezar", sign_in: "Iniciar sesión", sign_up: "Registrarse", logout: "Cerrar sesión",
    dashboard: "Panel", settings: "Ajustes", billing: "Suscripción",
    email: "Correo", password: "Contraseña", full_name: "Nombre completo", company: "Empresa",
    hero_pill: "SO de construcción AI · 14 días gratis",
    hero_title_a: "El", hero_title_b: "sistema operativo", hero_title_c: "de precisión para obras modernas.",
    hero_desc: "Registros de obra verificados por IA, asistencia con rostro + GPS, y alertas de seguridad en tiempo real — todo en una plataforma industrial, hecha para constructores globales.",
    start_free_14: "Empieza gratis por 14 días",
    stat_ai_models: "Modelos AI especializados", stat_languages: "Idiomas soportados", stat_servers: "Servidores a gestionar", stat_uptime: "SLA de tiempo activo",
    features_h_a: "Cada disciplina.", features_h_b: "Un centro de mando.",
    pricing_h_a: "Precios para equipos", pricing_h_b: "de todos los tamaños.",
    plan: "Plan", monthly: "Mensual", yearly: "Anual", subscribe_now: "Suscribirse", choose_plan: "Elige tu plan", start_with: "Empieza con",
    badge_popular: "Más popular",
    about: "Sobre", contact: "Contacto", privacy: "Privacidad", terms: "Términos", refund: "Reembolso",
  }),

  fr: stub({
    get_started: "Commencer", sign_in: "Connexion", sign_up: "S'inscrire", logout: "Déconnexion",
    dashboard: "Tableau de bord", settings: "Paramètres", billing: "Abonnement",
    email: "E-mail", password: "Mot de passe", full_name: "Nom complet", company: "Entreprise",
    hero_pill: "OS de construction IA · 14 jours gratuits",
    hero_title_a: "Le", hero_title_b: "système d'exploitation", hero_title_c: "de précision pour les chantiers modernes.",
    hero_desc: "Journaux de chantier vérifiés par IA, présence par visage + GPS, et alertes de sécurité en temps réel — dans une plateforme industrielle, pensée pour les constructeurs du monde entier.",
    start_free_14: "Essai gratuit 14 jours",
    plan: "Forfait", monthly: "Mensuel", yearly: "Annuel", subscribe_now: "S'abonner", choose_plan: "Choisir un forfait",
    about: "À propos", contact: "Contact", privacy: "Confidentialité", terms: "Conditions", refund: "Remboursement",
  }),

  de: stub({
    get_started: "Loslegen", sign_in: "Anmelden", sign_up: "Registrieren", logout: "Abmelden",
    dashboard: "Übersicht", settings: "Einstellungen", billing: "Abonnement",
    email: "E-Mail", password: "Passwort", full_name: "Vollständiger Name", company: "Unternehmen",
    hero_pill: "KI-Bau-OS · 14 Tage kostenlos",
    hero_title_a: "Das", hero_title_b: "Präzisions-OS", hero_title_c: "für moderne Baustellen.",
    hero_desc: "KI-verifizierte Baustellen-Logs, Gesichts- + GPS-Anwesenheit und Echtzeit-Sicherheitswarnungen — in einer industriellen Plattform.",
    start_free_14: "14 Tage kostenlos starten",
    plan: "Plan", monthly: "Monatlich", yearly: "Jährlich", subscribe_now: "Abonnieren", choose_plan: "Plan wählen",
    about: "Über uns", contact: "Kontakt", privacy: "Datenschutz", terms: "AGB", refund: "Rückerstattung",
  }),

  ar: stub({
    get_started: "ابدأ", sign_in: "تسجيل الدخول", sign_up: "اشترك", logout: "خروج",
    dashboard: "لوحة القيادة", settings: "الإعدادات", billing: "الاشتراك",
    email: "البريد الإلكتروني", password: "كلمة السر", full_name: "الاسم الكامل", company: "الشركة",
    hero_pill: "نظام تشغيل بناء بالذكاء الاصطناعي · 14 يومًا مجانًا",
    hero_title_a: "نظام", hero_title_b: "التشغيل الدقيق", hero_title_c: "لمواقع البناء الحديثة.",
    hero_desc: "سجلات موقع بالذكاء الاصطناعي، حضور بالوجه + GPS، وتنبيهات أمان في الوقت الفعلي.",
    start_free_14: "ابدأ مجانًا لمدة 14 يومًا",
    plan: "الخطة", monthly: "شهري", yearly: "سنوي", subscribe_now: "اشترك الآن", choose_plan: "اختر خطتك",
    about: "من نحن", contact: "اتصل", privacy: "الخصوصية", terms: "الشروط", refund: "استرداد",
  }),

  zh: stub({
    get_started: "开始", sign_in: "登录", sign_up: "注册", logout: "退出",
    dashboard: "仪表板", settings: "设置", billing: "订阅",
    email: "邮箱", password: "密码", full_name: "全名", company: "公司",
    hero_pill: "AI 建筑操作系统 · 14 天免费试用",
    hero_title_a: "现代建筑工地的", hero_title_b: "精准", hero_title_c: "操作系统。",
    hero_desc: "AI 核验的工地日志、人脸 + GPS 考勤和实时安全警报 — 一个工业级平台。",
    start_free_14: "免费试用 14 天",
    plan: "计划", monthly: "月度", yearly: "年度", subscribe_now: "立即订阅", choose_plan: "选择计划",
    about: "关于", contact: "联系", privacy: "隐私", terms: "条款", refund: "退款",
  }),

  pt: stub({
    get_started: "Começar", sign_in: "Entrar", sign_up: "Cadastre-se", logout: "Sair",
    dashboard: "Painel", settings: "Configurações", billing: "Assinatura",
    email: "E-mail", password: "Senha", full_name: "Nome completo", company: "Empresa",
    hero_pill: "SO de construção IA · 14 dias grátis",
    hero_title_a: "O", hero_title_b: "sistema operacional", hero_title_c: "de precisão para obras modernas.",
    hero_desc: "Registros de obra verificados por IA, presença com rosto + GPS, e alertas de segurança em tempo real.",
    start_free_14: "Começar grátis por 14 dias",
    plan: "Plano", monthly: "Mensal", yearly: "Anual", subscribe_now: "Assinar", choose_plan: "Escolha seu plano",
    about: "Sobre", contact: "Contato", privacy: "Privacidade", terms: "Termos", refund: "Reembolso",
  }),

  ja: stub({
    get_started: "はじめる", sign_in: "ログイン", sign_up: "登録", logout: "ログアウト",
    dashboard: "ダッシュボード", settings: "設定", billing: "サブスク",
    email: "メール", password: "パスワード", full_name: "氏名", company: "会社",
    hero_pill: "AI建設OS · 14日間無料",
    hero_title_a: "現代の建設現場のための", hero_title_b: "精密", hero_title_c: "オペレーティングシステム。",
    hero_desc: "AI検証済みサイトログ、顔+GPS勤怠、リアルタイム安全アラート。",
    start_free_14: "14日間無料で始める",
    plan: "プラン", monthly: "月額", yearly: "年額", subscribe_now: "今すぐ登録", choose_plan: "プランを選ぶ",
    about: "会社概要", contact: "お問い合わせ", privacy: "プライバシー", terms: "規約", refund: "返金",
  }),

  ta: stub({
    get_started: "தொடங்குங்கள்", sign_in: "உள்நுழை", sign_up: "பதிவு", logout: "வெளியேறு",
    dashboard: "டாஷ்போர்டு", settings: "அமைப்புகள்", billing: "சந்தா",
    hero_pill: "AI கட்டுமான OS · 14 நாட்கள் இலவசம்",
    hero_title_a: "நவீன கட்டுமான தளங்களுக்கான", hero_title_b: "துல்லிய", hero_title_c: "இயக்க முறைமை.",
    hero_desc: "AI-சரிபார்க்கப்பட்ட தள பதிவுகள், முகம் + GPS தொழிலாளர் வருகை, மற்றும் நேரடி பாதுகாப்பு எச்சரிக்கைகள்.",
    start_free_14: "14 நாட்கள் இலவசம் தொடங்குங்கள்",
    plan: "திட்டம்", monthly: "மாதாந்திர", yearly: "வருடாந்திர", subscribe_now: "சந்தா", choose_plan: "உங்கள் திட்டம்",
    about: "எங்களை பற்றி", contact: "தொடர்பு", privacy: "தனியுரிமை", terms: "விதிமுறைகள்", refund: "மறுபணம்",
  }),

  te: stub({ get_started: "ప్రారంభించండి", sign_in: "సైన్ ఇన్", sign_up: "సైన్ అప్", dashboard: "డాష్‌బోర్డ్",
    hero_title_a: "ఆధునిక నిర్మాణ సైట్‌ల కోసం", hero_title_b: "ఖచ్చితమైన", hero_title_c: "ఆపరేటింగ్ సిస్టమ్.",
    start_free_14: "14 రోజులు ఉచితంగా ప్రారంభించండి", plan: "ప్లాన్", monthly: "నెలవారీ", yearly: "వార్షిక" }),

  bn: stub({ get_started: "শুরু করুন", sign_in: "সাইন ইন", sign_up: "সাইন আপ", dashboard: "ড্যাশবোর্ড",
    hero_title_a: "আধুনিক নির্মাণ সাইটের জন্য", hero_title_b: "নির্ভুলতা", hero_title_c: "অপারেটিং সিস্টেম।",
    start_free_14: "14 দিন বিনামূল্যে শুরু করুন", plan: "প্ল্যান", monthly: "মাসিক", yearly: "বার্ষিক" }),

  gu: stub({ get_started: "શરૂ કરો", sign_in: "સાઇન ઇન", sign_up: "સાઇન અપ", dashboard: "ડેશબોર્ડ",
    hero_title_a: "આધુનિક બાંધકામ સાઇટ્સ માટે", hero_title_b: "ચોકસાઈ", hero_title_c: "ઓપરેટિંગ સિસ્ટમ.",
    start_free_14: "14 દિવસ મફત શરૂ કરો", monthly: "માસિક", yearly: "વાર્ષિક" }),

  kn: stub({ get_started: "ಪ್ರಾರಂಭಿಸಿ", sign_in: "ಸೈನ್ ಇನ್", sign_up: "ಸೈನ್ ಅಪ್", dashboard: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    hero_title_a: "ಆಧುನಿಕ ನಿರ್ಮಾಣ ತಾಣಗಳಿಗಾಗಿ", hero_title_b: "ನಿಖರತೆಯ", hero_title_c: "ಆಪರೇಟಿಂಗ್ ಸಿಸ್ಟಮ್.",
    start_free_14: "14 ದಿನ ಉಚಿತವಾಗಿ ಪ್ರಾರಂಭಿಸಿ" }),

  ml: stub({ get_started: "ആരംഭിക്കുക", sign_in: "സൈൻ ഇൻ", sign_up: "സൈൻ അപ്പ്", dashboard: "ഡാഷ്ബോർഡ്",
    hero_title_a: "ആധുനിക നിർമ്മാണ സൈറ്റുകൾക്കായി", hero_title_b: "കൃത്യതയുള്ള", hero_title_c: "ഓപ്പറേറ്റിംഗ് സിസ്റ്റം.",
    start_free_14: "14 ദിവസം സൗജന്യമായി ആരംഭിക്കുക" }),

  pa: stub({ get_started: "ਸ਼ੁਰੂ ਕਰੋ", sign_in: "ਸਾਈਨ ਇਨ", sign_up: "ਸਾਈਨ ਅੱਪ", dashboard: "ਡੈਸ਼ਬੋਰਡ",
    hero_title_a: "ਆਧੁਨਿਕ ਨਿਰਮਾਣ ਸਾਈਟਾਂ ਲਈ", hero_title_b: "ਸਟੀਕ", hero_title_c: "ਆਪਰੇਟਿੰਗ ਸਿਸਟਮ.",
    start_free_14: "14 ਦਿਨ ਮੁਫ਼ਤ ਸ਼ੁਰੂ ਕਰੋ" }),

  or: stub({ get_started: "ଆରମ୍ଭ କରନ୍ତୁ", sign_in: "ସାଇନ୍ ଇନ୍", sign_up: "ସାଇନ୍ ଅପ୍", dashboard: "ଡ୍ୟାସବୋର୍ଡ",
    hero_title_a: "ଆଧୁନିକ ନିର୍ମାଣ ସାଇଟ ପାଇଁ", hero_title_b: "ସଠିକ", hero_title_c: "ଅପରେଟିଂ ସିଷ୍ଟମ୍.",
    start_free_14: "14 ଦିନ ମାଗଣା ଆରମ୍ଭ କରନ୍ତୁ" }),
};
